import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';
import { UserRole } from '../../types';
import { hasPermission } from '../../utils/roleBasedAccess';

interface RecruitmentMetric {
  label: string;
  value: string | number;
  change?: number;
  changeType?: 'positive' | 'negative' | 'neutral';
  unit?: string;
  drillThroughPath?: string;
  minRecords?: number; // For privacy threshold
}

interface RecruitmentInsightCardProps {
  title: string;
  subtitle?: string;
  metrics: RecruitmentMetric[];
  sourceLabel?: string;
  lastUpdated?: Date;
  showDrillThrough?: boolean;
  roleScopeLabel?: string;
  className?: string;
}

const InsightCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${Shadows.md};
    border-color: ${Colors.primary}20;
  }
`;

const CardHeader = styled.div`
  margin-bottom: ${Spacing.md}px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm}px;
`;

const CardTitle = styled.h3`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const SourceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  background: ${Colors.info}10;
  color: ${Colors.info};
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
`;

const CardSubtitle = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin: 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.md}px;
`;

const MetricItem = styled.div<{ clickable?: boolean }>`
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  padding: ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 0.2s ease;
  
  ${props => props.clickable && `
    &:hover {
      background: ${Colors.surfaceHover};
      transform: translateY(-1px);
    }
  `}
`;

const MetricValue = styled.div`
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
  display: flex;
  align-items: baseline;
  gap: ${Spacing.xs}px;
`;

const MetricUnit = styled.span`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.normal};
  color: ${Colors.textSecondary};
`;

const MetricLabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricChange = styled.div<{ type: 'positive' | 'negative' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  margin-top: ${Spacing.xs}px;
  color: ${props => 
    props.type === 'positive' ? Colors.success :
    props.type === 'negative' ? Colors.danger :
    Colors.textTertiary
  };
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${Spacing.md}px;
  border-top: 1px solid ${Colors.borderLight};
`;

const UpdatedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const FreshnessDot = styled.div<{ fresh: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.fresh ? Colors.success : Colors.warning};
`;

const DrillThroughLink = styled.button`
  background: none;
  border: none;
  color: ${Colors.primary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.xs}px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primary}10;
  }
`;

const PermissionTooltip = styled.div`
  position: absolute;
  top: ${Spacing.sm}px;
  right: ${Spacing.sm}px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${Colors.borderLight};
  color: ${Colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xs}px;
  cursor: help;
  
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: ${Spacing.xs}px;
    padding: ${Spacing.sm}px;
    background: ${Colors.textPrimary};
    color: ${Colors.textOnPrimary};
    border-radius: ${BorderRadius.sm}px;
    font-size: ${FontSizes.xs}px;
    white-space: nowrap;
    z-index: 100;
  }
`;

const PrivacyMask = styled.div`
  background: ${Colors.backgroundDark};
  border: 1px dashed ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  padding: ${Spacing.md}px;
  text-align: center;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
  font-style: italic;
`;

const RecruitmentInsightCard: React.FC<RecruitmentInsightCardProps> = ({
  title,
  subtitle,
  metrics,
  sourceLabel = 'Recruitment Pipeline',
  lastUpdated = new Date(),
  showDrillThrough = true,
  roleScopeLabel,
  className
}) => {
  const navigate = useNavigate();
  const currentRole = useSelector((state: RootState) => state.user.currentRole);
  
  // Check if user has permission to view recruitment data
  const canViewRecruitmentData = hasPermission(currentRole, 'canViewRecruitmentData');
  
  // Calculate data freshness (fresh if updated within last 4 hours)
  const isFresh = (Date.now() - lastUpdated.getTime()) < 4 * 60 * 60 * 1000;
  
  // Format time difference
  const formatTimeDiff = (date: Date): string => {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };
  
  // Handle metric drill-through
  const handleMetricClick = (metric: RecruitmentMetric) => {
    if (!metric.drillThroughPath || !canViewRecruitmentData) return;
    
    // Navigate with context preserved
    navigate(metric.drillThroughPath, {
      state: {
        fromRole: currentRole,
        fromDashboard: window.location.pathname,
        metric: metric.label,
        scopeLabel: roleScopeLabel
      }
    });
  };
  
  // Handle main drill-through
  const handleDrillThrough = () => {
    if (!canViewRecruitmentData) return;
    
    // Navigate to recruiter dashboard with role context
    const targetPath = currentRole === 'HR_LEADER' 
      ? '/requisitions?view=strategic'
      : '/requisitions?view=team';
      
    navigate(targetPath, {
      state: {
        fromRole: currentRole,
        fromDashboard: window.location.pathname,
        scopeLabel: roleScopeLabel
      }
    });
  };
  
  // Apply privacy thresholds
  const shouldMaskMetric = (metric: RecruitmentMetric): boolean => {
    if (!metric.minRecords) return false;
    if (typeof metric.value !== 'number') return false;
    return metric.value < metric.minRecords;
  };
  
  // Filter metrics based on role permissions
  const filteredMetrics = metrics.filter(metric => {
    // Apply role-based filtering rules
    if (currentRole === 'MANAGER') {
      // Managers don't see market availability or diversity metrics
      const restrictedLabels = ['Market Availability', 'Diversity Pipeline'];
      return !restrictedLabels.includes(metric.label);
    }
    return true;
  });
  
  if (!canViewRecruitmentData) {
    return null; // Don't show card if user lacks permission
  }
  
  return (
    <InsightCard className={className}>
      <PermissionTooltip 
        data-tooltip={`Data visible per your ${currentRole.toLowerCase().replace('_', ' ')} access level`}
      >
        i
      </PermissionTooltip>
      
      <CardHeader>
        <HeaderRow>
          <CardTitle>{title}</CardTitle>
          <SourceBadge>
            <span>{sourceLabel}</span>
          </SourceBadge>
        </HeaderRow>
        {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        {roleScopeLabel && (
          <CardSubtitle style={{ 
            fontSize: FontSizes.xs + 'px', 
            fontStyle: 'italic',
            marginTop: Spacing.xs + 'px'
          }}>
            Scope: {roleScopeLabel}
          </CardSubtitle>
        )}
      </CardHeader>
      
      <MetricsGrid>
        {filteredMetrics.map((metric, index) => {
          const isMasked = shouldMaskMetric(metric);
          
          if (isMasked) {
            return (
              <MetricItem key={index}>
                <MetricLabel>{metric.label}</MetricLabel>
                <PrivacyMask>
                  Insufficient data
                  <br />
                  <span style={{ fontSize: FontSizes.xs + 'px' }}>
                    (min. {metric.minRecords} records)
                  </span>
                </PrivacyMask>
              </MetricItem>
            );
          }
          
          return (
            <MetricItem
              key={index}
              clickable={!!metric.drillThroughPath && showDrillThrough}
              onClick={() => handleMetricClick(metric)}
            >
              <MetricValue>
                {metric.value}
                {metric.unit && <MetricUnit>{metric.unit}</MetricUnit>}
              </MetricValue>
              <MetricLabel>{metric.label}</MetricLabel>
              {metric.change !== undefined && metric.changeType && (
                <MetricChange type={metric.changeType}>
                  <span>
                    {metric.changeType === 'positive' ? '↑' : 
                     metric.changeType === 'negative' ? '↓' : '→'}
                  </span>
                  <span>{Math.abs(metric.change)}%</span>
                </MetricChange>
              )}
            </MetricItem>
          );
        })}
      </MetricsGrid>
      
      <CardFooter>
        <UpdatedLabel>
          <FreshnessDot fresh={isFresh} />
          <span>Updated {formatTimeDiff(lastUpdated)}</span>
        </UpdatedLabel>
        {showDrillThrough && (
          <DrillThroughLink onClick={handleDrillThrough}>
            View Details →
          </DrillThroughLink>
        )}
      </CardFooter>
    </InsightCard>
  );
};

export default RecruitmentInsightCard;